import axiosInstance from 'integration/api/axiosInstance';
import {
  AppointmentBriefResponseDto,
  AppointmentCreationDto,
  AppointmentCreationResponseDto,
  AppointmentRequestResponseDto,
  AppointmentResponseDto,
  AppointmentStatusUpdateDto,
  AppointmentUpdateDto,
  AppointmentUpdateResponseDto,
  CommonEntitiesListResponse,
  PatientSearchResponseDto,
} from '@docbay/schemas';
import { PaginationProps } from 'common/types/pagination';

interface Props {
  patientId: string;
  startDateTime?: string;
  endDateTime?: string;
}

interface WaitingListProps {
  page: number;
  limit: number;
  clinicIds?: string[];
}

/**
 * API class that provides methods to interact with appointments.
 */
export const AppointmentsApi = {
  /**
   * Fetches a brief list of patient appointments.
   *
   * @param params - The method parameters.
   * @returns A promise that resolves to an array of AppointmentBriefResponseDto objects.
   */
  fetchPatientAppointmentsBrief: (params: Props) =>
    axiosInstance.get<AppointmentBriefResponseDto[]>(
      '/appointments/appointments-brief',
      {
        params,
      },
    ),

  /**
   * Fetches the appointments waiting list.
   *
   * @param params - The method parameters.
   * @returns A promise that resolves to an array of AppointmentRequestResponseDto objects.
   */
  fetchAppointmentsWaitingList: (params: WaitingListProps) =>
    axiosInstance.get('/appointments/waiting-list', {
      params,
    }),

  /**
   * Creates a new appointment.
   *
   * @param data - The appointment creation data.
   * @returns A promise that resolves to an AppointmentCreationResponseDto object.
   */
  createAppointment: (data: AppointmentCreationDto) =>
    axiosInstance.post<AppointmentCreationResponseDto>('/appointments', data),

  /**
   * Fetches a list of patients based on the search query.
   *
   * @param params - The method parameters.
   * @returns A promise that resolves to an array of PatientSearchResponseDto objects.
   */
  fetchAppointmentPatients: (params: { search: string }) =>
    axiosInstance.get<PatientSearchResponseDto[]>('/patients/search', {
      params,
    }),

  /**
   * Deletes the specified appointment.
   *
   * @param id - The ID of the appointment to delete.
   * @returns A promise that resolves when the deletion is complete.
   */
  deleteAppointment: (id: string) =>
    axiosInstance.delete(`/appointments/${id}`),

  /**
   * Fetches the details of a specific appointment.
   *
   * @param id - The ID of the appointment to fetch.
   * @returns A promise that resolves to an AppointmentResponseDto object.
   */
  fetchAppointmentById: (id: string) =>
    axiosInstance.get<AppointmentResponseDto>(
      `/appointments/${id}?include=patient,clinicProfessional,appointmentType,documents,appointmentType.specialization`,
    ),

  /**
   * Updates the specified appointment with new data.
   *
   * @param id - The ID of the appointment to update.
   * @param data - The updated appointment data.
   * @returns A promise that resolves to an AppointmentUpdateResponseDto object.
   */
  editAppointment: (id: string, data: AppointmentUpdateDto) =>
    axiosInstance.patch<AppointmentUpdateResponseDto>(
      `/appointments/${id}`,
      data,
    ),

  /**
   * Fetches the teleconsultation connection data for a specific appointment.
   *
   * @param id - The ID of the appointment to fetch the connection data for.
   * @returns A promise that resolves when the connection data is retrieved.
   */
  getTeleconsultationConnectionData: (id: string) =>
    axiosInstance.get(`/appointments/${id}/connection-data`),

  /**
   * Creates a new teleconsultation chat for a specific appointment.
   *
   * @param id - The ID of the appointment to create the chat for.
   * @returns A promise that resolves when the chat is created.
   */
  createTeleconsultationChat: (id: string) =>
    axiosInstance.post(`/appointments/${id}/chat`),

  /**
   * Ends the specified appointment and generates an invoice.
   *
   * @param id - The ID of the appointment to end.
   * @returns A promise that resolves when the appointment is ended.
   */
  endAppointment: (id: string) => {
    return axiosInstance.post(`/appointments/${id}/complete`);
  },

  /**
   * Updates the status of the specified appointment with new data.
   *
   * @param id - The ID of the appointment to update.
   * @param data - The updated appointment status data.
   * @returns A promise that resolves when the status is updated.
   */
  updateAppointmentStatus: (id: string, data: AppointmentStatusUpdateDto) =>
    axiosInstance.patch<AppointmentStatusUpdateDto>(
      `/appointments/${id}/status`,
      data,
    ),

  /**
   * Fetches a list of appointment requests based on pagination parameters.
   *
   * @param params - The method parameters.
   * @returns A promise that resolves to an array of AppointmentRequestResponseDto objects.
   */
  fetchAppointmentRequestList: (params: PaginationProps) =>
    axiosInstance.get<
      CommonEntitiesListResponse<AppointmentRequestResponseDto>
    >('/appointments/appointment-requests', {
      params,
    }),

  /**
   * Deletes the specified appointment request.
   *
   * @param id - The ID of the appointment request to delete.
   * @returns A promise that resolves when the deletion is complete.
   */
  deleteAppointmentRequest: (id: string) =>
    axiosInstance.delete(`/appointments/appointment-requests/${id}`),
};
